import type { StudioScenarioPresetId } from "./scenarioPresets";
import type { VetCanStudioStepId } from "./studioReadModel";
import {
  STUDIO_SCENARIO_PACKS,
  getStudioScenarioPack,
  isStudioScenarioPackId,
  type StudioScenarioPack,
  type StudioScenarioPackId,
} from "./studioScenarioPacks";

export const STUDIO_DEMO_LAUNCHER_PRESETS = STUDIO_SCENARIO_PACKS.map((pack) => {
  switch (pack.id) {
    case "general_service_business":
      return {
        ...pack,
        summary:
          "Show a polished operator-ready front desk workflow for high-trust service businesses that need cleaner booking conversion, stronger follow-up discipline, and visible rollout guardrails.",
        boundaryNote:
          "Showcase stays inside the Studio draft layer and internal preview lane only. It does not expose public tenant activation or live routing changes.",
      };
    case "clinic_demo":
      return {
        ...pack,
        summary:
          "Present the clinic-safe white-label flow as a premium front-desk rehearsal with clean booking, payment-safe support, and explicit handoff boundaries.",
        boundaryNote:
          "Clinic showcase content remains white-label packaging only. Public tenant behavior and live clinic routing stay separate from this Studio surface.",
      };
    case "creator_demo":
      return {
        ...pack,
        summary:
          "Frame Studio as a premium creator-concierge layer for inbound triage, brand-safe handoff, and polished preview voice review without widening live capabilities.",
        boundaryNote:
          "Creator showcase stays inside supported preview, tone, and handoff boundaries only. No public activation or cross-tenant behavior is implied.",
      };
    case "franchise_multi_site":
      return {
        ...pack,
        summary:
          "Demonstrate multi-site operational clarity with reusable routing posture, callback discipline, and launch-control visibility while keeping every rollout step operator-gated.",
        boundaryNote:
          "Launch-control truth and rollout blockers remain visible for every showcase variant. Multi-site packaging does not imply live tenant promotion or shared tenant state.",
      };
    default:
      return pack;
  }
});

export type StudioDemoLauncherId = StudioScenarioPackId;
export type StudioDemoLauncherPreset = StudioScenarioPack;

export { isStudioScenarioPackId as isStudioDemoLauncherId };

export function getStudioDemoLauncherPreset(
  presetId: StudioDemoLauncherId
): StudioDemoLauncherPreset {
  const refinedPack = STUDIO_DEMO_LAUNCHER_PRESETS.find((pack) => pack.id === presetId);
  return refinedPack || getStudioScenarioPack(presetId);
}

export function buildStudioDemoPreviewHref(params: {
  demoId: StudioDemoLauncherId;
  returnStep?: VetCanStudioStepId;
  scenarioId?: StudioScenarioPresetId;
}) {
  const preset = getStudioDemoLauncherPreset(params.demoId);
  const search = new URLSearchParams();

  search.set("returnStep", params.returnStep || preset.entryStep);
  search.set("scenario", params.scenarioId || preset.previewScenarioId);
  search.set("demo", preset.id);

  return `/studio/preview?${search.toString()}`;
}